/**
 * DocumentComparisonUploadZone - Drag-and-drop zone for uploading a zip file.
 *
 * Validates that only .zip files are accepted. Delegates the actual S3 upload
 * to the caller via `onFileSelected` so the parent can use the existing blob
 * upload pipeline.
 */

import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useCallback, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

interface DocumentComparisonUploadZoneProps {
  onFileSelected: (file: File) => void;
  disabled?: boolean;
}

export const DocumentComparisonUploadZone: React.FC<
  DocumentComparisonUploadZoneProps
> = ({ onFileSelected, disabled }) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isZip = (file: File): boolean => {
    const mime = file.type;
    return (
      mime === 'application/zip' ||
      mime === 'application/x-zip-compressed' ||
      mime === 'application/x-zip' ||
      file.name.toLowerCase().endsWith('.zip')
    );
  };

  const handleFile = useCallback(
    (file: File) => {
      if (!isZip(file)) return;
      onFileSelected(file);
    },
    [onFileSelected],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (disabled) return;
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [disabled, handleFile],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) setIsDragging(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback(() => setIsDragging(false), []);

  const handleClick = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      // Reset so the same file can be re-selected
      e.target.value = '';
    },
    [handleFile],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const dropZoneClasses = (() => {
    if (disabled)
      return 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60';
    if (isDragging) return 'cursor-copy border-indigo-400 bg-indigo-50/50';
    return 'cursor-pointer border-gray-300 bg-white hover:border-indigo-300 hover:bg-indigo-50/30';
  })();

  return (
    <div
      role="button"
      tabIndex={0}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-all duration-200',
        dropZoneClasses,
      )}
    >
      <div
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-full transition-colors',
          isDragging ? 'bg-indigo-100' : 'bg-gray-100',
        )}
      >
        <ArrowUpTrayIcon
          className={cn(
            'h-5 w-5 transition-colors',
            isDragging ? 'text-indigo-600' : 'text-gray-400',
          )}
        />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-gray-700">
          {isDragging ? 'Drop zip file here' : 'Upload external CMS zip'}
        </p>
        <p className="mt-0.5 text-xs text-gray-400">
          Drag & drop or click to select a .zip file
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed"
        className="hidden"
        onChange={handleInputChange}
      />
    </div>
  );
};
